import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { FormFields } from '@orderform/widgets';
import { OrderformFascade } from '../../../fascades/orderform.fascade';
import { PrimeNGConfig } from 'primeng/api';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  skip,
  startWith,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { OrderformService } from '../../../services/orderform.service';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { getPageBuilderElementVariant } from '../../../misc/pagebuilder';
import { OrderFormFieldBlurPayload, OrderFormFieldFocusPayload } from 'libs/tracking-core/src/lib/types/events/OrderForm';

type PrimeHourFormat = '12' | '24';

@Component({
  selector: 'orderform-generator-input',
  templateUrl: './generator-input.component.html',
  styleUrls: ['./generator-input.component.scss'],
  // needed to pass formgroup validation throw components
  changeDetection: ChangeDetectionStrategy.Default,
})
export class GeneratorInputComponent implements OnInit, OnDestroy {
  debounceTime = 250;
  private _destory$ = new Subject<void>();

  @Input() inputId = '';
  @Input() field: FormFields.Field;
  @Input() formGroupRef: UntypedFormGroup;
  @Output() readonly focused = new EventEmitter<OrderFormFieldFocusPayload>();
  @Output() readonly blurred = new EventEmitter<OrderFormFieldBlurPayload>();

  trackByIndex = (i) => i;

  formControlMapData = new FormControl();

  mapOptions = (data: Array<FormFields.SelectOption | '-'>) => {
    return data.map((e) => {
      const key = Object.keys(e)[0];
      return {
        name: key ? Object.values(e)[0] : $localize`:@@select.label.please_select_option:Bitte wählen`,
        key,
      };
    });
  };

  getFormControl = () => {
    return this.formGroupRef.get(this.field.postname);
  };

  dateTodayMinMaxValue = new Date();
  dateFormat = null;
  hourFormat: PrimeHourFormat = null;

  preferredPhoneCountries = toSignal(
    this._orderformService.getCountries$.pipe(
      map((list) => {
        const normalListStart = list.indexOf('-');
        return normalListStart >= 1 ? list.slice(0, normalListStart).map((item) => item[0].toLowerCase()) : [];
      })
    ),
    { initialValue: [] }
  );

  defaultPhoneCountry = toSignal(
    this._orderFormFascade.order$.pipe(
      map(
        (order) =>
          order.order.shipping_address['buyer_address_private_country'] ??
          order.order.shipping_address['buyer_address_country']
      ),
      filter((country) => typeof country === 'string'),
      map((country) => country.toLowerCase())
    ),
    { initialValue: 'de' }
  );

  loadCountriesForSelect$ = this._orderformService.getCountries$.pipe(
    map((countries) => {
      return countries.map((e) => {
        if (e === '-') {
          return {
            name: e,
            key: null,
          };
        } else {
          return {
            name: e[1],
            key: e[0],
          };
        }
      });
    }),
    tap((availableCountries) => {
      /**
       * Wenn vom BE ein Wert für Country zurück kommt, der nicht in der Liste
       * verfügbar ist, dann soll der erste verfügbare Wert aus der Liste genommen werden
       */
      if (this.field.type === 'country') {
        const initControl = this.getFormControl();
        const initValue = initControl.value;
        if (!availableCountries.find((availableCountry) => availableCountry.key === initValue)) {
          if (availableCountries[0]) {
            initControl.setValue(availableCountries[0].key);
            initControl.markAsDirty();
            initControl.markAsTouched();
          }
        }
      }
    }),
    shareReplay(1)
  );

  showDropdown = false;
  focus$ = new BehaviorSubject<boolean>(false);
  @ViewChildren('radioInput') radioInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @Output() readonly inputFocusToggle = this.focus$.asObservable();

  constructor(
    private _orderFormFascade: OrderformFascade,
    private _orderformService: OrderformService,
    private _primeNGConfig: PrimeNGConfig
  ) {
    this._primeNGConfig.setTranslation({
      dayNames: [
        ,
        $localize`:@@core.calendar.sunday:Sonntag`,
        $localize`:@@core.calendar.monday:Montag`,
        $localize`:@@core.calendar.tuesday:Dienstag`,
        $localize`:@@core.calendar.wednesday:Mittwoch`,
        $localize`:@@core.calendar.thursday:Donnerstag`,
        $localize`:@@core.calendar.friday:Freitag`,
        $localize`:@@core.calendar.saturday:Samstag`,
      ],
      dayNamesShort: [
        $localize`:@@core.calendar.sunday_short:Son`,
        $localize`:@@core.calendar.monday_short:Mon`,
        $localize`:@@core.calendar.tuesday_short:Die`,
        $localize`:@@core.calendar.wednesday_short:Mit`,
        $localize`:@@core.calendar.thursday_short:Don`,
        $localize`:@@core.calendar.friday_short:Fre`,
        $localize`:@@core.calendar.saturday_short:Sam`,
      ],
      dayNamesMin: [
        $localize`:@@core.calendar.sunday_min:So`,
        $localize`:@@core.calendar.monday_min:Mo`,
        $localize`:@@core.calendar.tuesday_min:Di`,
        $localize`:@@core.calendar.wednesday_min:Mi`,
        $localize`:@@core.calendar.thursday_min:Do`,
        $localize`:@@core.calendar.friday_min:Fr`,
        $localize`:@@core.calendar.saturday_min:Sa`,
      ],
      monthNames: [
        $localize`:@@core.calendar.janurary:Januar`,
        $localize`:@@core.calendar.february:Februar`,
        $localize`:@@core.calendar.march:März`,
        $localize`:@@core.calendar.april:April`,
        $localize`:@@core.calendar.may:Mai`,
        $localize`:@@core.calendar.june:Juni`,
        $localize`:@@core.calendar.july:Juli`,
        $localize`:@@core.calendar.august:August`,
        $localize`:@@core.calendar.september:September`,
        $localize`:@@core.calendar.october:Oktober`,
        $localize`:@@core.calendar.november:November`,
        $localize`:@@core.calendar.december:Dezember`,
      ],
      monthNamesShort: [
        $localize`:@@core.calendar.janurary_short:Jan`,
        $localize`:@@core.calendar.february_short:Feb`,
        $localize`:@@core.calendar.march_short:Mär`,
        $localize`:@@core.calendar.april_short:Apr`,
        $localize`:@@core.calendar.may_short:Mai`,
        $localize`:@@core.calendar.june_short:Jun`,
        $localize`:@@core.calendar.july_short:Jul`,
        $localize`:@@core.calendar.august_short:Aug`,
        $localize`:@@core.calendar.september_short:Sep`,
        $localize`:@@core.calendar.october_short:Okt`,
        $localize`:@@core.calendar.november_short:Nov`,
        $localize`:@@core.calendar.december_short:Dez`,
      ],
      today: $localize`:@@core.calendar.today:Heute`,
      clear: $localize`:@@core.common.clear:Zurücksetzen`,
      weekHeader: $localize`:@@core.calendar.week_short:Wk`,
    });
    this.dateFormat = $localize`:@@core.calendar.date_format:dd.mm.yy`;
    this.hourFormat = <PrimeHourFormat>$localize`:use only 24 or 12@@core.calendar.hour_format:24`.trim();
  }

  ngOnInit(): void {
    this.getFormControl()
      .valueChanges.pipe(
        startWith(this.getFormControl().value),
        distinctUntilChanged(),
        skip(1),
        filter(() => this.getFormControl().value && this.field.onchange?.includes('buy_reload_if_country_changed'))
      )
      .pipe(debounceTime(this.debounceTime), takeUntil(this._destory$))
      .subscribe((newValue) => {
        firstValueFrom(this._orderFormFascade.update$());
      });
  }

  ngAfterViewInit(): void {
    if (!this.radioInputs || this.radioInputs.length === 0) return;
    this.radioInputs.forEach((ref: ElementRef<HTMLInputElement>) => {
      const input = ref.nativeElement;
      const variant = getPageBuilderElementVariant(input);
      const newId = `${input.id}${variant ? `-${variant}` : ''}`;
      input.id = newId;

      const label = input.parentElement?.querySelector('label');
      if (label) {
        label.setAttribute('for', newId);
      }
    });
  }

  onBlur(field: FormFields.Field) {
    this.focus$.next(false);

    const control = this.getFormControl();
    const value = control?.value;

    const hadError = control?.invalid && (control.dirty || control.touched);
    const fieldValueProvided =
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(typeof value === 'object' && Object.keys(value).length === 0);

    this.blurred.emit({
      fieldName: field.postname,
      hadError,
      fieldValueProvided,
    });
  }

  onFocus(field: FormFields.Field) {
    this.focus$.next(true);
    this.focused.emit({ fieldName: field.postname });
  }

  onRadioChange(field: any) {
    switch (field.postname) {
      case 'buyer_address_i_order_as':
        firstValueFrom(this._orderFormFascade.update$());
        break;
    }
    this.getFormControl().markAsTouched();
  }

  onCheckBoxChange() {
    const control = this.getFormControl();
    if (this.field.is_checked === control.value) {
      control.markAsUntouched();
    } else {
      control.markAsTouched();
    }
  }

  validateTaxId() {
    firstValueFrom(this._orderFormFascade.validate$());
  }

  onEnter(field: FormFields.Field) {
    if (field.postname === 'buyer_address_tax_id') {
      this.validateTaxId();
    }
  }

  isTextInput(field) {
    return (
      field.type === 'text' ||
      field.type === 'first_name' ||
      field.type === 'last_name' ||
      field.type === 'title' ||
      field.type === 'company' ||
      field.type === 'street' ||
      field.type === 'street_name' ||
      field.type === 'street_number' ||
      field.type === 'street2' ||
      field.type === 'city' ||
      field.type === 'zipcode' ||
      field.type === 'state' ||
      field.type === 'domain' ||
      field.type === 'phone_no'
    );
  }

  /**
   * Diese methode wird nur bei Inputs mit type dropdown  mit Multi-Select option genutzt.
   * Hier werden dann im BE Checkboxen für die Mehrfachauswahl genutzt.
   * Generell wird hier auf alle möglichen Auswahl-Optionen geprüft und entsprechend wird dann das
   * Feld mit dem Optionen auf Valide oder entsprechend invalide gesetzt
   * @param field
   */
  watchForCheckboxChanges$(field: FormFields.Field) {
    if (!field.is_required) {
      return of(true);
    }

    const optionsToCheck = field.options as Array<FormFields.SelectOption>;
    /**
     * get values of all options as observable array
     */
    const watchCheckboxesValues$: Observable<string | boolean>[] = optionsToCheck
      .map((option) => {
        const [key] = Object.keys(option);
        return field.postname + '_' + key;
      })
      .map((postname) => {
        return this.formGroupRef.get(postname);
      })
      .map((control) => control.valueChanges.pipe(startWith(control.value)));

    return combineLatest(watchCheckboxesValues$).pipe(
      map((valuesOfOptions) => {
        if (valuesOfOptions.find((value) => !!value)) {
          /**
           * Set main select a valid value, of any of the options has an valid value
           */
          this.formGroupRef.get(field.postname).patchValue('select_checkboxes_valid');
        } else {
          this.formGroupRef.get(field.postname).patchValue(null);
        }
        return true;
      })
    );
  }

  ngOnDestroy() {
    this._destory$.next();
    this._destory$.complete();
  }
}
